/**
 * My Account Page - Customer Profile Management
 * 
 * Features:
 * - Edit profile (name, email)
 * - Upload avatar
 * - Manage shipping addresses
 * - Change password
 * - MFA settings
 * - Login activity
 * - Session management
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Camera, MapPin, Plus, Trash2, Edit2, Check, X, 
  Lock, Shield, ChevronRight, Loader2, ArrowLeft, Star, Home, Building2,
  Activity, Monitor, Smartphone, LogOut, Clock, Globe, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { profileAPI, authAPI } from '../utils/api';
import { toast } from 'sonner';
import MFASetup from './Admin/MFASetup';

const MyAccountPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, checkAuth } = useAuth();
  const fileInputRef = useRef(null);
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Login activity state
  const [showActivity, setShowActivity] = useState(false);
  const [loginActivity, setLoginActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Sessions state
  const [showSessions, setShowSessions] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: '',
    first_name: '',
    last_name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
    is_default: false
  });
  const [savingAddress, setSavingAddress] = useState(false);
  
  // MFA state
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  
  // Load profile on mount
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/my-account' } } });
      return;
    }
    
    if (isAuthenticated) {
      loadProfile();
      loadAddresses();
    }
  }, [isAuthenticated, authLoading, navigate]);
  
  const loadProfile = async () => {
    try {
      const data = await profileAPI.getProfile();
      setProfile(data);
      setEditData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone: data.phone || ''
      });
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAddresses = async () => {
    try {
      const data = await profileAPI.getAddresses();
      setAddresses(data.addresses || []);
    } catch (err) {
      console.error('Failed to load addresses:', err);
    } finally {
      setLoadingAddresses(false);
    }
  };
  
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await profileAPI.updateProfile(editData);
      await loadProfile();
      setEditMode(false);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    try {
      await profileAPI.uploadAvatar(file);
      await loadProfile();
      await checkAuth(); // Refresh user context
      toast.success('Avatar updated');
    } catch (err) {
      toast.error('Failed to upload avatar');
    }
  };
  
  const handleDeleteAvatar = async () => {
    try {
      await profileAPI.deleteAvatar();
      await loadProfile();
      await checkAuth();
      toast.success('Avatar removed');
    } catch (err) {
      toast.error('Failed to remove avatar');
    }
  };
  
  const handleChangeEmail = async () => {
    if (!newEmail || !emailPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setChangingEmail(true);
    try {
      await profileAPI.changeEmail(newEmail, emailPassword);
      await loadProfile();
      await checkAuth();
      setShowEmailChange(false);
      setNewEmail('');
      setEmailPassword('');
      toast.success('Email updated');
    } catch (err) {
      toast.error(err.message || 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };
  
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed');
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };
  
  // Address handlers
  const resetAddressForm = () => {
    setAddressForm({
      label: '',
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      company: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      phone: profile?.phone || '',
      is_default: addresses.length === 0
    });
    setEditingAddress(null);
  };
  
  const handleAddAddress = () => {
    resetAddressForm();
    setShowAddressForm(true);
  };
  
  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      label: address.label,
      first_name: address.first_name,
      last_name: address.last_name,
      company: address.company || '',
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country,
      phone: address.phone || '',
      is_default: address.is_default
    });
    setShowAddressForm(true);
  };
  
  const handleSaveAddress = async () => {
    const required = ['label', 'first_name', 'last_name', 'address_line1', 'city', 'state', 'postal_code'];
    for (const field of required) {
      if (!addressForm[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }
    
    setSavingAddress(true);
    try {
      if (editingAddress) {
        await profileAPI.updateAddress(editingAddress.id, addressForm);
        toast.success('Address updated');
      } else {
        await profileAPI.createAddress(addressForm);
        toast.success('Address added');
      }
      await loadAddresses();
      setShowAddressForm(false);
      resetAddressForm();
    } catch (err) {
      toast.error(err.message || 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };
  
  const handleDeleteAddress = async (addressId) => {
    if (!confirm('Delete this address?')) return;
    
    try {
      await profileAPI.deleteAddress(addressId);
      await loadAddresses();
      toast.success('Address deleted');
    } catch (err) {
      toast.error('Failed to delete address');
    }
  };
  
  const handleSetDefault = async (addressId) => {
    try {
      await profileAPI.setDefaultAddress(addressId);
      await loadAddresses();
      toast.success('Default address updated');
    } catch (err) {
      toast.error('Failed to set default address');
    }
  };
  
  // Login activity handlers
  const loadLoginActivity = async () => {
    setLoadingActivity(true);
    try {
      const data = await profileAPI.getLoginActivity(20);
      setLoginActivity(data.activities || []);
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };
  
  // Sessions handlers
  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const data = await profileAPI.getSessions();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };
  
  const handleRevokeSession = async (sessionId) => {
    try {
      await profileAPI.revokeSession(sessionId);
      await loadSessions();
      toast.success('Session revoked');
    } catch (err) {
      toast.error('Failed to revoke session');
    }
  };
  
  const handleRevokeAllSessions = async () => {
    if (!confirm('Sign out of all other devices?')) return;
    try {
      await profileAPI.revokeAllSessions();
      await loadSessions();
      toast.success('All other sessions revoked');
    } catch (err) {
      toast.error('Failed to revoke sessions');
    }
  };
  
  const handleSendVerification = async () => {
    try {
      await profileAPI.sendVerificationEmail();
      toast.success('Verification email sent');
    } catch (err) {
      toast.error(err.message || 'Failed to send verification email');
    }
  };
  
  // Helper to parse user agent
  const parseUserAgent = (ua) => {
    if (!ua) return { device: 'Unknown', browser: 'Unknown' };
    const isMobile = /mobile|android|iphone|ipad/i.test(ua);
    let browser = 'Unknown';
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    return { device: isMobile ? 'Mobile' : 'Desktop', browser };
  };
  
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">My Account</h1>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Section */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
            {!editMode && (
              <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {profile?.avatar_url || profile?.picture ? (
                    <img 
                      src={profile.avatar_url || profile.picture} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700"
                  data-testid="avatar-upload-btn"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
              </div>
              {(profile?.avatar_url || profile?.picture) && (
                <button
                  onClick={handleDeleteAvatar}
                  className="text-sm text-red-600 hover:underline"
                >
                  Remove photo
                </button>
              )}
            </div>
            
            {/* Profile Fields */}
            <div className="flex-1 space-y-4">
              {editMode ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        value={editData.first_name}
                        onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={editData.last_name}
                        onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveProfile} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">
                      {profile?.first_name || profile?.name || 'Not set'} {profile?.last_name || ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{profile?.email}</p>
                      <button
                        onClick={() => setShowEmailChange(true)}
                        className="text-sm text-blue-600 hover:underline"
                        data-testid="change-email-btn"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                  {profile?.phone && (
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">{profile.phone}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Email Change Modal */}
          {showEmailChange && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-md w-full">
                <h3 className="text-lg font-semibold mb-4">Change Email Address</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-email">New Email</Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email-password">Current Password</Label>
                    <Input
                      id="email-password"
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      placeholder="Enter your password"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleChangeEmail} disabled={changingEmail}>
                      {changingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Change Email
                    </Button>
                    <Button variant="outline" onClick={() => setShowEmailChange(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Security Section */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Security</h2>
          
          <div className="space-y-3">
            {/* Password */}
            <button
              onClick={() => setShowPasswordChange(!showPasswordChange)}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="change-password-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Lock className="h-5 w-5 text-gray-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Password</p>
                  <p className="text-sm text-gray-500">Change your password</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            {showPasswordChange && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleChangePassword} disabled={changingPassword}>
                    {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Update Password
                  </Button>
                  <Button variant="outline" onClick={() => setShowPasswordChange(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* MFA */}
            <button
              onClick={() => setShowMfaSetup(true)}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="mfa-settings-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Shield className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-gray-500">
                    {profile?.mfa_settings?.enabled ? 'Enabled' : 'Add extra security'}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            {/* Login Activity */}
            <button
              onClick={() => { setShowActivity(!showActivity); if (!showActivity) loadLoginActivity(); }}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="login-activity-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Activity className="h-5 w-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Login Activity</p>
                  <p className="text-sm text-gray-500">Recent sign-in history</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            {showActivity && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                {loadingActivity ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : loginActivity.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No login activity yet</p>
                ) : (
                  loginActivity.slice(0, 10).map((activity, i) => {
                    const { device, browser } = parseUserAgent(activity.user_agent);
                    return (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <div className={`p-1.5 rounded-full ${activity.success ? 'bg-green-100' : 'bg-red-100'}`}>
                          {activity.success ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {activity.success ? 'Signed in' : 'Failed login attempt'}
                          </p>
                          <p className="text-gray-500">
                            {device} • {browser} • {activity.ip_address || 'Unknown IP'}
                          </p>
                          <p className="text-xs text-gray-400">
                            {activity.created_at ? new Date(activity.created_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            
            {/* Active Sessions */}
            <button
              onClick={() => { setShowSessions(!showSessions); if (!showSessions) loadSessions(); }}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="active-sessions-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Monitor className="h-5 w-5 text-orange-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Active Sessions</p>
                  <p className="text-sm text-gray-500">Manage your logged-in devices</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            {showSessions && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                {loadingSessions ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : sessions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No active sessions</p>
                ) : (
                  <>
                    {sessions.map((session) => {
                      const { device, browser } = parseUserAgent(session.user_agent);
                      return (
                        <div key={session.session_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-3">
                            {device === 'Mobile' ? (
                              <Smartphone className="h-5 w-5 text-gray-400" />
                            ) : (
                              <Monitor className="h-5 w-5 text-gray-400" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{browser} on {device}</p>
                                {session.is_current && (
                                  <Badge variant="outline" className="text-xs">Current</Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">{session.ip_address || 'Unknown IP'}</p>
                              <p className="text-xs text-gray-400">
                                Started {session.created_at ? new Date(session.created_at).toLocaleDateString() : ''}
                              </p>
                            </div>
                          </div>
                          {!session.is_current && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevokeSession(session.session_id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <LogOut className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    {sessions.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRevokeAllSessions}
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Sign out all other sessions
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Email Verification Status */}
          {profile && !profile.email_verified && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-amber-800">Your email is not verified</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleSendVerification}>
                Verify Now
              </Button>
            </div>
          )}
        </div>
        
        {/* Shipping Addresses Section */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Shipping Addresses</h2>
            <Button variant="outline" size="sm" onClick={handleAddAddress} data-testid="add-address-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Address
            </Button>
          </div>
          
          {loadingAddresses ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : addresses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>No shipping addresses yet</p>
              <Button variant="link" onClick={handleAddAddress}>Add your first address</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {addresses.map((address) => (
                <div
                  key={address.id}
                  className={`p-4 rounded-lg border ${address.is_default ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {address.label.toLowerCase().includes('home') ? (
                          <Home className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Building2 className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="font-medium">{address.label}</span>
                        {address.is_default && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Default</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {address.first_name} {address.last_name}
                      </p>
                      {address.company && (
                        <p className="text-sm text-gray-600">{address.company}</p>
                      )}
                      <p className="text-sm text-gray-600">{address.address_line1}</p>
                      {address.address_line2 && (
                        <p className="text-sm text-gray-600">{address.address_line2}</p>
                      )}
                      <p className="text-sm text-gray-600">
                        {address.city}, {address.state} {address.postal_code}
                      </p>
                      {address.phone && (
                        <p className="text-sm text-gray-500 mt-1">{address.phone}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {!address.is_default && (
                        <button
                          onClick={() => handleSetDefault(address.id)}
                          className="p-2 text-gray-400 hover:text-blue-600"
                          title="Set as default"
                        >
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEditAddress(address)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAddress(address.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Address Form Modal */}
          {showAddressForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">
                  {editingAddress ? 'Edit Address' : 'Add New Address'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="addr-label">Address Label *</Label>
                    <Input
                      id="addr-label"
                      value={addressForm.label}
                      onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                      placeholder="e.g., Home, Office"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="addr-first-name">First Name *</Label>
                      <Input
                        id="addr-first-name"
                        value={addressForm.first_name}
                        onChange={(e) => setAddressForm({ ...addressForm, first_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="addr-last-name">Last Name *</Label>
                      <Input
                        id="addr-last-name"
                        value={addressForm.last_name}
                        onChange={(e) => setAddressForm({ ...addressForm, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="addr-company">Company</Label>
                    <Input
                      id="addr-company"
                      value={addressForm.company}
                      onChange={(e) => setAddressForm({ ...addressForm, company: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="addr-line1">Address Line 1 *</Label>
                    <Input
                      id="addr-line1"
                      value={addressForm.address_line1}
                      onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="addr-line2">Address Line 2</Label>
                    <Input
                      id="addr-line2"
                      value={addressForm.address_line2}
                      onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })}
                      placeholder="Apt, Suite, etc."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="addr-city">City *</Label>
                      <Input
                        id="addr-city"
                        value={addressForm.city}
                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="addr-state">State *</Label>
                      <Input
                        id="addr-state"
                        value={addressForm.state}
                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="addr-postal">Postal Code *</Label>
                      <Input
                        id="addr-postal"
                        value={addressForm.postal_code}
                        onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="addr-phone">Phone</Label>
                      <Input
                        id="addr-phone"
                        value={addressForm.phone}
                        onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={addressForm.is_default}
                      onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Set as default shipping address</span>
                  </label>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveAddress} disabled={savingAddress}>
                      {savingAddress ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {editingAddress ? 'Save Changes' : 'Add Address'}
                    </Button>
                    <Button variant="outline" onClick={() => { setShowAddressForm(false); resetAddressForm(); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Order History Link */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <Link
            to="/orders"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium">Order History</p>
                <p className="text-sm text-gray-500">View your past orders</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </Link>
        </div>
      </div>
      
      {/* MFA Setup Modal */}
      <MFASetup
        isOpen={showMfaSetup}
        onClose={() => setShowMfaSetup(false)}
        onComplete={() => loadProfile()}
      />
    </div>
  );
};

export default MyAccountPage;
